import ApplicationService, { ApplicationServiceProps } from "@mirage/services/ApplicationService";
import { MirageRoutingService } from "@mirage/services/MirageRoutingService";
import { userState } from "@mirage/store/UserStore";
import { useCallback, useMemo } from "react";
import { useSetRecoilState } from "recoil";
import useSessionTimeout from "./useSessionTimeout";
import { SessionManager } from "@mirage/manager/SessionManager";
import { useStableNavigate } from "./useStableNavigate";
import { IMirageApplications, MirageApplicationTypes } from "@shared/mirage/core/IMirageApplications";

const router = new MirageRoutingService();
const disableSessionTimeoutApplications = [MirageApplicationTypes.publicportal, MirageApplicationTypes.prePublicPortalWestLindsay];

export const useInitializeUserSession = () => {
    const [setSessionTimeout] = useSessionTimeout();
    const navigate = useStableNavigate();
    const appServiceProps = { logoutNavigate: () => navigate(router.LoginRoute()), setSessionTimeout } as ApplicationServiceProps;
    const appService = useMemo(() => new ApplicationService(appServiceProps), []);
    const setUser = useSetRecoilState(userState);
    const getLoggedInUser = useCallback(() => appService.getLoggedInUser(), []);

    const setInitialUser = useCallback(
        async (app: IMirageApplications) => {
            // if the tab was memory saved by the browser, clear the session timeouts
            if ((document as any).wasDiscarded) {
                SessionManager.clearSessionTimeouts();
            }
            const user = getLoggedInUser();
            const userId = user?.id;
            const expiryDate = user?.expiryDate;
            if (!userId || !expiryDate) {
                await appService.logout();
                return;
            }
            const result = await appService.getUserPermissions({ app });
            const permissions = result.permissions;
            if (!user) {
                await appService.logout();
            }
            setUser({ ...user, permissions });
            if (disableSessionTimeoutApplications.includes(app as any)) {
                return;
            }
            SessionManager.setSessionTimeoutBuffer(user.sessionTimeoutBuffer);
            appService.prolongRoutine();
        },
        [appService, getLoggedInUser, setUser]
    );

    return setInitialUser;
};
